
import { useLanguage } from '../../../hooks/useLanguage';

const Testimonials = () => {
  const { currentLanguage } = useLanguage();

  // Import testimonials based on current language
  const getTestimonials = async () => {
    switch (currentLanguage) {
      case 'fr':
        return (await import('../../../mocks/fr/common')).testimonials;
      case 'en':
        return (await import('../../../mocks/en/common')).testimonials;
      case 'ro':
        return (await import('../../../mocks/ro/common')).testimonials;
      default:
        return (await import('../../../mocks/ru/common')).testimonials;
    }
  };

  const getTexts = () => {
    switch (currentLanguage) {
      case 'fr':
        return {
          title: 'Témoignages Clients',
          subtitle: 'Ce que disent nos clients satisfaits'
        };
      case 'en':
        return {
          title: 'Client Testimonials',
          subtitle: 'What our satisfied clients say'
        };
      case 'ro':
        return {
          title: 'Mărturii Clienți',
          subtitle: 'Ce spun clienții noștri mulțumiți'
        };
      default:
        return {
          title: 'Отзывы Клиентов',
          subtitle: 'Что говорят наши довольные клиенты'
        };
    }
  };

  const texts = getTexts();

  // Default testimonials for immediate rendering
  const defaultTestimonials = [
    {
      id: 1,
      name: 'Sarah Johnson',
      location: currentLanguage === 'fr' ? 'Genève' : currentLanguage === 'ru' ? 'Женева' : 'Geneva',
      rating: 5,
      text: currentLanguage === 'fr' 
        ? 'Travaux de rénovation exceptionnels dans notre appartement. L\'équipe était professionnelle, ponctuelle et a livré une qualité exceptionnelle. Je recommande vivement Alpbuild.'
        : currentLanguage === 'ru'
        ? 'Выдающиеся ремонтные работы в нашей квартире. Команда была профессиональной, пунктуальной и обеспечила исключительное качество. Настоятельно рекомендую Alpbuild.'
        : 'Outstanding renovation work on our apartment. The team was professional, punctual, and delivered exceptional quality. Highly recommend Alpbuild for any construction project.',
      project: currentLanguage === 'fr' ? 'Rénovation d\'appartement' : currentLanguage === 'ru' ? 'Ремонт квартиры' : 'Apartment renovation'
    },
    {
      id: 2,
      name: 'Michael Weber',
      location: currentLanguage === 'fr' ? 'Lausanne' : currentLanguage === 'ru' ? 'Лозанна' : 'Lausanne',
      rating: 5,
      text: currentLanguage === 'fr'
        ? 'Excellent travail de façade et d\'isolation. Le projet a été terminé dans les délais et le budget. Très satisfait de l\'approche professionnelle.'
        : currentLanguage === 'ru'
        ? 'Отличные фасадные работы и утепление. Проект был завершен вовремя и в рамках бюджета. Очень доволен профессиональным подходом.'
        : 'Excellent facade work and insulation. The project was completed on time and within budget. Very satisfied with the professional approach and attention to detail.',
      project: currentLanguage === 'fr' ? 'Isolation de façade' : currentLanguage === 'ru' ? 'Утепление фасада' : 'Facade insulation'
    },
    {
      id: 3,
      name: 'Elena Rossi',
      location: currentLanguage === 'fr' ? 'Montreux' : currentLanguage === 'ru' ? 'Монтрё' : 'Montreux',
      rating: 5,
      text: currentLanguage === 'fr'
        ? 'Travaux de peinture et finition parfaits dans notre nouvelle maison. Travail propre et précis avec des matériaux de haute qualité.'
        : currentLanguage === 'ru'
        ? 'Идеальные малярные и отделочные работы в нашем новом доме. Чистая, точная работа с высококачественными материалами.'
        : 'Perfect painting and finishing work in our new home. Clean, precise work with high-quality materials. The team exceeded our expectations.',
      project: currentLanguage === 'fr' ? 'Peinture intérieure' : currentLanguage === 'ru' ? 'Внутренняя покраска' : 'Interior painting'
    },
    {
      id: 4,
      name: 'David Martinez',
      location: currentLanguage === 'fr' ? 'Vaud' : currentLanguage === 'ru' ? 'Во' : 'Vaud',
      rating: 5,
      text: currentLanguage === 'fr'
        ? 'Rénovation complète du bureau gérée professionnellement du début à la fin. Excellente communication et résultats exceptionnels.'
        : currentLanguage === 'ru'
        ? 'Полная реновация офиса была выполнена профессионально от начала до конца. Отличная коммуникация и превосходные результаты.'
        : 'Complete office renovation was handled professionally from start to finish. Great communication and excellent results. Will definitely work with Alpbuild again.',
      project: currentLanguage === 'fr' ? 'Rénovation de bureau' : currentLanguage === 'ru' ? 'Реновация офиса' : 'Office renovation'
    }
  ];

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {texts.title}
          </h2>
          <p className="text-xl text-gray-600">
            {texts.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {defaultTestimonials.map((testimonial) => (
            <div key={testimonial.id} className="bg-white rounded-xl p-6 shadow-lg">
              <div className="flex items-center mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                ))}
              </div>
              <p className="text-gray-600 mb-6 italic text-sm">
                "{testimonial.text}"
              </p>
              <div className="border-t pt-4">
                <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                <p className="text-sm text-gray-500">{testimonial.location}</p>
                <p className="text-sm text-blue-600 mt-1">{testimonial.project}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Admin Note - Hidden in production */}
        <div className="mt-12 p-6 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="text-lg font-semibold text-blue-900 mb-2">
            {currentLanguage === 'fr' ? 'Note pour l\'administrateur' : 
             currentLanguage === 'ru' ? 'Заметка для администратора' : 
             'Admin Note'}
          </h3>
          <p className="text-blue-800 text-sm">
            {currentLanguage === 'fr' 
              ? 'Pour modifier ces témoignages, éditez les fichiers dans src/mocks/[langue]/common.ts. Chaque témoignage contient: nom, lieu, note (1-5), texte du témoignage et type de projet.'
              : currentLanguage === 'ru'
              ? 'Для изменения этих отзывов отредактируйте файлы в src/mocks/[язык]/common.ts. Каждый отзыв содержит: имя, местоположение, рейтинг (1-5), текст отзыва и тип проекта.'
              : 'To edit these testimonials, modify the files in src/mocks/[language]/common.ts. Each testimonial contains: name, location, rating (1-5), testimonial text, and project type.'
            }
          </p>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
